import React, { useState, useEffect } from 'react';
import { beebaseService } from '../services/firebase';
import { useAuth } from '../hooks/useAuth';
import { apiaryService } from '../services/firebase';
import ApiariesMap from '../features/apiaries/ApiariesMap';

// Interface matching our Cloud Function data structure
interface BeeBaseAlert {
  id: string;
  date: string | null;
  region: string | null;
  disease: string | null;
  location: string | null;
  notes: string | null;
  source: string;
  fetchedAt: string;
  status: string;
}

const BeeBaseAlertsPage: React.FC = () => {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState<BeeBaseAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userRegions, setUserRegions] = useState<string[]>([]);
  const [showAllAlerts, setShowAllAlerts] = useState(true);
  const [showMap, setShowMap] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const fetchAlertsAndRegions = async () => {
      try {
        setLoading(true);
        
        // Get user's apiary regions if logged in
        if (user) {
          console.log('🔍 Getting user apiaries to determine regions...');
          const userApiaries = await apiaryService.getUserApiaries(user.uid);
          
          // Extract regions from apiary locations (basic mapping)
          const regions = userApiaries.map(apiary => {
            const location = apiary.location.toLowerCase();
            
            // Basic UK region mapping from location strings
            if (location.includes('devon') || location.includes('exeter')) return 'Devon';
            if (location.includes('somerset') || location.includes('taunton') || location.includes('bath')) return 'Somerset';
            if (location.includes('cornwall') || location.includes('truro') || location.includes('falmouth')) return 'Cornwall';
            if (location.includes('yorkshire') || location.includes('york') || location.includes('leeds') || location.includes('harrogate')) return 'Yorkshire';
            if (location.includes('kent') || location.includes('canterbury') || location.includes('dover')) return 'Kent';
            if (location.includes('london') || location.includes('greater london')) return 'London';
            if (location.includes('surrey') || location.includes('guildford')) return 'Surrey';
            if (location.includes('sussex') || location.includes('brighton')) return 'Sussex';
            if (location.includes('hampshire') || location.includes('winchester')) return 'Hampshire';
            if (location.includes('dorset') || location.includes('bournemouth')) return 'Dorset';
            
            // Default fallback - extract first word as potential region
            return location.split(' ')[0] || 'Unknown';
          }).filter((region, index, self) => self.indexOf(region) === index); // Remove duplicates
          
          setUserRegions(regions);
          console.log('📍 User regions detected:', regions);
        }
        
        console.log('🔍 Fetching BeeBase alerts...');
        const activeAlerts = await beebaseService.getAllActiveAlerts();
        console.log('📊 Found alerts:', activeAlerts.length, activeAlerts);
        setAlerts(activeAlerts);
        
        // Set last updated timestamp
        setLastUpdated(new Date().toLocaleString());
      } catch (err) {
        console.error('❌ Error fetching BeeBase alerts:', err);
        setError('Failed to load alerts');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    };

    fetchAlertsAndRegions();
  }, [user]);

  // Manual refresh function
  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    
    try {
      console.log('🔄 Manual refresh of BeeBase alerts...');
      const activeAlerts = await beebaseService.getAllActiveAlerts();
      console.log('📊 Refreshed alerts:', activeAlerts.length, activeAlerts);
      setAlerts(activeAlerts);
      setLastUpdated(new Date().toLocaleString());
    } catch (err) {
      console.error('❌ Error refreshing BeeBase alerts:', err);
      setError('Failed to refresh alerts');
    } finally {
      setRefreshing(false);
    }
  };

  // Filter alerts based on user preference
  const filteredAlerts = showAllAlerts 
    ? alerts 
    : alerts.filter(alert => 
        alert.region && userRegions.some(region => 
          region.toLowerCase() === alert.region?.toLowerCase()
        )
      );

  // Calculate stats from filtered data
  const totalActiveAlerts = filteredAlerts.length;
  const resolvedThisMonth = 0; // TODO: Add logic for resolved alerts
  const riskLevel = totalActiveAlerts > 5 ? 'High' : totalActiveAlerts > 2 ? 'Medium' : 'Low';



  return (
    <div className="page-content">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <h2>🐝 BeeBase Disease Alerts</h2>
        <button 
          onClick={handleRefresh} 
          disabled={refreshing || loading}
          className="refresh-btn"
        >
          <span className={refreshing ? 'refresh-icon' : ''}>
            🔄
          </span>
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>
      
      <p>Stay informed about disease outbreaks and pest alerts in your area.</p>
      
      {lastUpdated && (
        <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '20px' }}>
          📅 Last updated: {lastUpdated}
        </p>
      )}
      
      {loading && <p>Loading alerts...</p>}
      {error && <p className="error">Error: {error}</p>}
      
      {/* Location Filter Controls */}
      {user && userRegions.length > 0 && (
        <div className="filter-controls" style={{ margin: '20px 0', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
          <h4>📍 Alert Filtering</h4>
          <p><strong>Your Regions:</strong> {userRegions.join(', ')}</p>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <label>
              <input 
                type="radio" 
                checked={showAllAlerts} 
                onChange={() => setShowAllAlerts(true)}
              />
              Show All UK Alerts ({alerts.length})
            </label>
            <label>
              <input 
                type="radio" 
                checked={!showAllAlerts} 
                onChange={() => setShowAllAlerts(false)}
              />
              Show My Regions Only ({filteredAlerts.length})
            </label>
          </div>
        </div>
      )}
      
      {!user && (
        <div className="info-banner" style={{ margin: '20px 0', padding: '15px', backgroundColor: '#e3f2fd', borderRadius: '8px' }}>
          <p><strong>💡 Tip:</strong> Log in and add your apiaries to get location-specific disease alerts for your regions!</p>
        </div>
      )}
      
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Active Alerts</h3>
          <div className="stat-number">{totalActiveAlerts}</div>
          <p>{showAllAlerts ? 'Across UK' : 'In your regions'}</p>
        </div>
        <div className="stat-card">
          <h3>Resolved This Month</h3>
          <div className="stat-number">{resolvedThisMonth}</div>
          <p>Recent clearances</p>
        </div>
        <div className="stat-card">
          <h3>Risk Level</h3>
          <div className="stat-number">{riskLevel}</div>
          <p>Current assessment</p>
        </div>
      </div>

      <div className="actions-grid">
        <button className="action-btn" onClick={() => setShowMap(!showMap)}>
          <span className="action-icon">🗺️</span>
          <span>{showMap ? 'Hide Disease Map' : 'View Disease Map'}</span>
        </button>
        <button className="action-btn">
          <span className="action-icon">🔔</span>
          <span>Set Alert Preferences</span>
        </button>
        <button className="action-btn">
          <span className="action-icon">📱</span>
          <span>Report Disease</span>
        </button>
      </div>

      {showMap && (
        <div style={{ margin: '20px 0' }}>
          <h3 style={{ marginBottom: '15px', color: '#333' }}>UK Disease Alert Map</h3>
          <div style={{ height: '500px', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <ApiariesMap 
              apiaries={[]} 
              forageMarkers={[]} 
              showBeeBaseAlerts={true}
              showRiskAssessment={false}
            />
          </div>
        </div>
      )}

      <div className="recent-activity">
        <h3>Recent Alerts {!showAllAlerts && '(Your Regions)'}</h3>
        <div className="activity-list">
          {!loading && filteredAlerts.length === 0 ? (
            showAllAlerts ? (
              <p className="no-activity">No active disease alerts in the UK. The system will automatically check for updates daily.</p>
            ) : (
              <div className="no-activity">
                <p>No disease alerts in your regions ({userRegions.join(', ')}).</p>
                <button onClick={() => setShowAllAlerts(true)} className="secondary-btn">
                  View All UK Alerts
                </button>
              </div>
            )
          ) : (
            filteredAlerts.map((alert) => (
              <div key={alert.id} className="alert-item">
                <div className="alert-header">
                  <h4>{alert.disease || 'Unknown Disease'}</h4>
                  <span className="alert-date">{alert.date || 'No date'}</span>
                </div>
                <div className="alert-details">
                  <p><strong>Region:</strong> {alert.region || 'Unknown'}</p>
                  {alert.location && <p><strong>Location:</strong> {alert.location}</p>}
                  {alert.notes && <p><strong>Notes:</strong> {alert.notes}</p>}
                  <span className={`alert-status ${alert.status.toLowerCase()}`}>
                    {alert.status}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default BeeBaseAlertsPage;
